import React, { useState } from 'react';
import {
  FiPlay, FiLoader, FiChevronRight, FiChevronDown, FiDownload,
  FiAlertTriangle, FiCheck, FiFileText, FiUsers, FiUpload, FiX,
} from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import analytics from '../utils/analytics';
import './InstantReview.css';

// Same color mapping as GrantAgent panel review
const REVIEWER_COLORS = {
  significance:       { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', dot: '#3b82f6' },
  intellectual_merit: { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', dot: '#3b82f6' },
  scientific_merit:   { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', dot: '#3b82f6' },
  research_plan:      { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8', dot: '#3b82f6' },
  innovation:         { bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3', dot: '#6366f1' },
  innovation_ip:      { bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3', dot: '#6366f1' },
  approach:           { bg: '#f5f3ff', border: '#ddd6fe', text: '#6d28d9', dot: '#8b5cf6' },
  approach_methods:   { bg: '#f5f3ff', border: '#ddd6fe', text: '#6d28d9', dot: '#8b5cf6' },
  investigator_env:   { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  feasibility:        { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  technical_feasibility: { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  team_execution:     { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  candidate:          { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  mentor_environment: { bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  training_activities:{ bg: '#ecfeff', border: '#a5f3fc', text: '#155e75', dot: '#06b6d4' },
  program_officer:    { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46', dot: '#10b981' },
  agency_alignment:   { bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46', dot: '#10b981' },
  commercial_potential:{ bg: '#ecfdf5', border: '#a7f3d0', text: '#065f46', dot: '#10b981' },
  broader_impacts:    { bg: '#fffbeb', border: '#fde68a', text: '#92400e', dot: '#f59e0b' },
  broader_impact:     { bg: '#fffbeb', border: '#fde68a', text: '#92400e', dot: '#f59e0b' },
  equity_reviewer:    { bg: '#fffbeb', border: '#fde68a', text: '#92400e', dot: '#f59e0b' },
  skeptic:            { bg: '#fff1f2', border: '#fecdd3', text: '#9f1239', dot: '#ef4444' },
};

const SEVERITY_META = {
  high:   { label: 'High',   cls: 'ir-severity--high'   },
  medium: { label: 'Medium', cls: 'ir-severity--medium' },
  low:    { label: 'Low',    cls: 'ir-severity--low'    },
};

const GRANT_TYPE_OPTIONS = [
  { group: 'NIH', options: [
    { value: 'NIH R01', label: 'NIH R01' },
    { value: 'NIH R21', label: 'NIH R21' },
    { value: 'NIH R03', label: 'NIH R03' },
    { value: 'NIH Career Development (K)', label: 'NIH K Award' },
    { value: 'NIH', label: 'NIH (other)' },
  ]},
  { group: 'NSF', options: [
    { value: 'NSF CAREER', label: 'NSF CAREER' },
    { value: 'NSF', label: 'NSF (other)' },
  ]},
  { group: 'SBIR / STTR', options: [
    { value: 'SBIR', label: 'SBIR' },
    { value: 'STTR', label: 'STTR' },
  ]},
  { group: 'Other Federal', options: [
    { value: 'DOE', label: 'DOE' },
    { value: 'DOT', label: 'DOT' },
    { value: 'federal research grant', label: 'General Federal' },
  ]},
];

export default function InstantReview({ user, onLogin, onStartGrantAgent } = {}) {
  const [draftText, setDraftText]   = useState('');
  const [nofoText, setNofoText]     = useState('');
  const [grantType, setGrantType]   = useState('');
  const [showNofo, setShowNofo]     = useState(false);
  const [running, setRunning]       = useState(false);
  const [error, setError]           = useState('');
  const [result, setResult]         = useState(null);
  const [expandedReviewers, setExpandedReviewers] = useState({});
  const [refsExpanded, setRefsExpanded] = useState(false);
  const [creating, setCreating]     = useState(false);
  const [draftFile, setDraftFile]   = useState(null);
  const [nofoFile, setNofoFile]     = useState(null);
  const [acceptedFiles, setAcceptedFiles] = useState([]);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [showRefs, setShowRefs]     = useState(false);
  const draftFileRef = React.useRef(null);
  const nofoFileRef = React.useRef(null);
  const acceptedFileRef = React.useRef(null);
  const rejectedFileRef = React.useRef(null);

  const ACCEPTED_TYPES = '.pdf,.docx,.doc,.txt,.md';
  const charCount = draftFile ? 0 : draftText.length;
  const canSubmit = (draftFile || draftText.trim().length >= 100) && !running;

  const runReview = async () => {
    if (!user) {
      if (onLogin) onLogin();
      return;
    }
    analytics.track('instant_review_submitted', {
      source: draftFile ? 'upload' : 'paste',
      grant_type: grantType || 'unknown',
      has_nofo: !!(nofoFile || (nofoText && nofoText.trim())),
      accepted_files: acceptedFiles.length,
      rejected_files: rejectedFiles.length,
    });
    setRunning(true);
    setError('');
    setResult(null);
    try {
      let res;
      const hasRefFiles = acceptedFiles.length > 0 || rejectedFiles.length > 0;
      if (draftFile || hasRefFiles) {
        // File upload path — multipart form (also used when reference files are present)
        const form = new FormData();
        if (draftFile) {
          form.append('file', draftFile);
        } else {
          // Package pasted text as a file for the upload endpoint
          const blob = new Blob([draftText.trim()], { type: 'text/plain' });
          form.append('file', blob, 'draft.txt');
        }
        if (nofoFile) {
          form.append('nofo_file', nofoFile);
        } else if (nofoText.trim()) {
          const nofoBlob = new Blob([nofoText.trim()], { type: 'text/plain' });
          form.append('nofo_file', nofoBlob, 'nofo.txt');
        }
        if (grantType) form.append('grant_type', grantType);
        acceptedFiles.forEach(f => form.append('accepted_files', f));
        rejectedFiles.forEach(f => form.append('rejected_files', f));
        res = await fetch(getApiEndpoint('/api/grant-proposals/instant-review-upload'), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: form,
        });
      } else {
        // Text paste path — JSON (no reference files)
        res = await fetch(getApiEndpoint('/api/grant-proposals/instant-review'), {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            draft_text: draftText.trim(),
            nofo_text: nofoText.trim(),
            grant_type: grantType,
          }),
        });
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Request failed (${res.status})`);
      }
      setResult(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const downloadMarkdown = () => {
    if (!result) return;
    const lines = [];
    const s = result.synthesis || {};
    lines.push('# Instant Panel Review\n');
    lines.push(`**Grant Type:** ${result.grant_type || 'Auto-detected'}`);
    lines.push(`**Run:** ${result.run_at ? new Date(result.run_at).toLocaleString() : 'Unknown'}`);
    lines.push(`**Overall:** ${s.overall_readiness || '---'}\n`);
    lines.push('---\n');
    if (s.top_strengths?.length) {
      lines.push('## Panel Strengths\n');
      s.top_strengths.forEach(st => lines.push(`- ${st}`));
      lines.push('');
    }
    if (s.ranked_issues?.length) {
      lines.push('## Priority Issues\n');
      s.ranked_issues.forEach((issue, i) => {
        lines.push(`### ${i + 1}. [${(issue.severity || '').toUpperCase()}] ${issue.section || ''}`);
        lines.push(issue.issue || '');
        if (issue.fix) lines.push(`**Fix:** ${issue.fix}`);
        if (issue.flagged_by?.length) lines.push(`**Flagged by:** ${issue.flagged_by.map(id => id.replace(/_/g, ' ')).join(', ')}`);
        lines.push('');
      });
    }
    (result.reviewers || []).forEach(r => {
      lines.push(`### ${r.label}`);
      if (r.overall_comment) lines.push(`> ${r.overall_comment}\n`);
      if (r.strengths?.length) {
        lines.push('**Strengths:**');
        r.strengths.forEach(st => lines.push(`- ${st}`));
        lines.push('');
      }
      if (r.issues?.length) {
        lines.push('**Issues:**');
        r.issues.forEach(issue => {
          lines.push(`- **[${(issue.severity || '').toUpperCase()}] ${issue.section || ''}** --- ${issue.issue}`);
          if (issue.fix) lines.push(`  Fix: ${issue.fix}`);
        });
        lines.push('');
      }
    });
    const refs = Array.isArray(result.references_used) ? result.references_used : [];
    if (refs.length > 0) {
      lines.push('## Reference Patterns\n');
      lines.push(`Grounded in ${refs.length} similar proposals.\n`);
      refs.forEach((ref, i) => {
        const bits = [
          ref.section_type,
          ref.grant_mechanism || null,
          `outcome: ${ref.outcome || 'unknown'}`,
          typeof ref.impact_score === 'number' ? `impact ${ref.impact_score}` : null,
          typeof ref.similarity === 'number' ? `similarity ${ref.similarity.toFixed(2)}` : null,
        ].filter(Boolean).join(' · ');
        lines.push(`${i + 1}. ${bits}`);
        if ((ref.critique_tags || []).length > 0) {
          lines.push(`   Concern tags: ${ref.critique_tags.join(', ')}`);
        }
      });
      lines.push('');
    }
    lines.push('---');
    lines.push('*Generated by Mnevia Instant Panel Review*');
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'instant_panel_review.md';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const fixIssues = async () => {
    if (!user) { if (onLogin) onLogin(); return; }
    if (!result || !onStartGrantAgent) return;
    // When the review ran against an uploaded file, the raw text lives on the
    // server-returned result, not in the textarea.
    const effectiveDraft = (draftText && draftText.trim()) || (result.extracted_draft_text || '').trim();
    const effectiveNofo = (nofoText && nofoText.trim()) || (result.extracted_nofo_text || '').trim();
    if (!effectiveDraft) {
      console.warn('[fixIssues] no draft text available', {
        textareaLen: draftText ? draftText.length : 0,
        resultKeys: Object.keys(result || {}),
        serverReturnedExtracted: 'extracted_draft_text' in (result || {}),
        extractedLen: (result.extracted_draft_text || '').length,
      });
      setError(
        'Could not recover the draft text from the uploaded file. '
        + 'Restart the backend and rerun the review, or paste the draft as text.'
      );
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(getApiEndpoint('/api/grant-proposals/from-instant-review'), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draft_text: effectiveDraft,
          nofo_text: effectiveNofo,
          grant_type: grantType || result.grant_type || '',
          panel_review: result,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Request failed (${res.status})`);
      }
      const proposal = await res.json();

      // Auto-iterate: behave as if the user landed on the Panel Review step and
      // clicked "Iterate draft". Failures here are non-fatal — the user can
      // retry iterate from the GrantAgent UI if AI tokens ran out, etc.
      try {
        const iterRes = await fetch(
          getApiEndpoint(`/api/grant-proposals/${proposal.id}/iterate`),
          {
            method: 'POST',
            headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          }
        );
        if (!iterRes.ok) {
          const data = await iterRes.json().catch(() => ({}));
          console.warn('[fixIssues] iterate failed, continuing to GrantAgent:', data.detail || iterRes.status);
        }
      } catch (iterErr) {
        console.warn('[fixIssues] iterate threw, continuing to GrantAgent:', iterErr);
      }

      onStartGrantAgent(proposal.id);
    } catch (e) {
      setError(e.message);
      setCreating(false);
    }
  };

  const highIssueCount = result
    ? (result.synthesis?.ranked_issues || []).filter(i => (i.severity || '').toLowerCase() === 'high').length
    : 0;

  return (
    <div className="ir-page ph-no-capture">
      <div className="ir-header">
        <div className="ir-header-text">
          <h1><FiUsers /> Instant Panel Review</h1>
          <p>Paste your draft. Get 6 expert reviewer reactions in 60 seconds.</p>
        </div>
      </div>

      <div className="ir-layout">
        {/* ── Input panel ── */}
        <div className={`ir-input-panel ${result ? 'ir-input-panel--compact' : ''}`}>
          <div className="ir-input-section">
            <label className="ir-label">
              <FiFileText /> Your Draft
              {!draftFile && <span className="ir-char-count">{charCount.toLocaleString()} chars</span>}
            </label>

            {draftFile ? (
              <div className="ir-file-badge">
                <FiFileText />
                <span className="ir-file-name">{draftFile.name}</span>
                <span className="ir-file-size">({(draftFile.size / 1024).toFixed(0)} KB)</span>
                <button className="ir-file-remove" onClick={() => { setDraftFile(null); if (draftFileRef.current) draftFileRef.current.value = ''; }}>
                  <FiX />
                </button>
              </div>
            ) : (
              <textarea
                className="ir-textarea"
                placeholder="Paste your Specific Aims, abstract, full proposal draft, or any section you want reviewed..."
                value={draftText}
                onChange={e => setDraftText(e.target.value)}
                rows={result ? 8 : 14}
              />
            )}

            <div className="ir-upload-row">
              <input
                ref={draftFileRef}
                type="file"
                accept={ACCEPTED_TYPES}
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) { setDraftFile(e.target.files[0]); setDraftText(''); } }}
              />
              <button
                className="ir-upload-btn"
                onClick={() => draftFileRef.current?.click()}
                title="Upload PDF, DOCX, or TXT"
              >
                <FiUpload /> {draftFile ? 'Replace file' : 'Or upload PDF / DOCX'}
              </button>
            </div>
          </div>

          {!showNofo && !nofoText && !nofoFile && (
            <button className="ir-toggle-nofo" onClick={() => setShowNofo(true)}>
              + Add NOFO / RFP (optional — paste text or upload file)
            </button>
          )}
          {(showNofo || nofoText || nofoFile) && (
            <div className="ir-input-section">
              <label className="ir-label">NOFO / RFP (optional)</label>
              {nofoFile ? (
                <div className="ir-file-badge">
                  <FiFileText />
                  <span className="ir-file-name">{nofoFile.name}</span>
                  <span className="ir-file-size">({(nofoFile.size / 1024).toFixed(0)} KB)</span>
                  <button className="ir-file-remove" onClick={() => { setNofoFile(null); if (nofoFileRef.current) nofoFileRef.current.value = ''; }}>
                    <FiX />
                  </button>
                </div>
              ) : (
                <textarea
                  className="ir-textarea ir-textarea--nofo"
                  placeholder="Paste the NOFO or RFP text for more targeted reviewer feedback..."
                  value={nofoText}
                  onChange={e => setNofoText(e.target.value)}
                  rows={4}
                />
              )}
              <input
                ref={nofoFileRef}
                type="file"
                accept={ACCEPTED_TYPES}
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) { setNofoFile(e.target.files[0]); setNofoText(''); } }}
              />
              {!nofoFile && (
                <button className="ir-upload-btn ir-upload-btn--sm" onClick={() => nofoFileRef.current?.click()}>
                  <FiUpload /> Upload NOFO file
                </button>
              )}
            </div>
          )}

          {!showRefs && !acceptedFiles.length && !rejectedFiles.length && (
            <button className="ir-toggle-nofo" onClick={() => setShowRefs(true)}>
              + Add winning / losing proposals (calibrates review to your program)
            </button>
          )}
          {(showRefs || acceptedFiles.length > 0 || rejectedFiles.length > 0) && (
            <div className="ir-input-section">
              <label className="ir-label">Reference Proposals (calibrate the review)</label>
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 8px' }}>
                Upload winning and/or losing proposals from the same program. The panel will calibrate its criteria based on what differentiated winners from losers.
              </p>

              {/* Accepted / Winning files */}
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#16a34a' }}>Winning proposals</span>
                {acceptedFiles.map((f, i) => (
                  <div key={i} className="ir-file-badge" style={{ marginTop: 4 }}>
                    <FiCheck style={{ color: '#16a34a' }} />
                    <span className="ir-file-name">{f.name}</span>
                    <span className="ir-file-size">({(f.size / 1024).toFixed(0)} KB)</span>
                    <button className="ir-file-remove" onClick={() => setAcceptedFiles(prev => prev.filter((_, j) => j !== i))}>
                      <FiX />
                    </button>
                  </div>
                ))}
                <input
                  ref={acceptedFileRef}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files.length) { setAcceptedFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; } }}
                />
                <button className="ir-upload-btn ir-upload-btn--sm" onClick={() => acceptedFileRef.current?.click()} style={{ marginTop: 4 }}>
                  <FiUpload /> Add winning proposal
                </button>
              </div>

              {/* Rejected / Losing files */}
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>Losing proposals</span>
                {rejectedFiles.map((f, i) => (
                  <div key={i} className="ir-file-badge" style={{ marginTop: 4 }}>
                    <FiAlertTriangle style={{ color: '#dc2626' }} />
                    <span className="ir-file-name">{f.name}</span>
                    <span className="ir-file-size">({(f.size / 1024).toFixed(0)} KB)</span>
                    <button className="ir-file-remove" onClick={() => setRejectedFiles(prev => prev.filter((_, j) => j !== i))}>
                      <FiX />
                    </button>
                  </div>
                ))}
                <input
                  ref={rejectedFileRef}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files.length) { setRejectedFiles(prev => [...prev, ...Array.from(e.target.files)]); e.target.value = ''; } }}
                />
                <button className="ir-upload-btn ir-upload-btn--sm" onClick={() => rejectedFileRef.current?.click()} style={{ marginTop: 4 }}>
                  <FiUpload /> Add losing proposal
                </button>
              </div>
            </div>
          )}

          <div className="ir-controls">
            <select
              className="ir-grant-select"
              value={grantType}
              onChange={e => setGrantType(e.target.value)}
            >
              <option value="">Auto-detect grant type</option>
              {GRANT_TYPE_OPTIONS.map(g => (
                <optgroup key={g.group} label={g.group}>
                  {g.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </optgroup>
              ))}
            </select>
            <button
              className="ir-run-btn"
              onClick={runReview}
              disabled={!canSubmit && !!user}
              title={!user ? 'Sign up free to run the review' : charCount < 100 ? 'Paste at least 100 characters of draft text' : ''}
            >
              {running
                ? <><FiLoader className="ir-spin" /> Reviewing...</>
                : !user
                  ? <><FiPlay /> Sign up free to review</>
                  : <><FiPlay /> Run Panel Review</>
              }
            </button>
          </div>

          {error && (
            <div className="ir-error">
              <FiAlertTriangle /> {error}
            </div>
          )}

          {!result && !running && (
            <div className="ir-info-cards">
              <div className="ir-info-card">
                <strong>How it works</strong>
                <p>Six expert reviewers --- calibrated to your grant type --- read your draft in parallel and return structured feedback with severity ratings and specific fixes.</p>
              </div>
              <div className="ir-info-card">
                <strong>Calibrate to your program</strong>
                <p>Upload winning and losing proposals from the same program. The panel derives program-specific evaluation criteria from what differentiated funded from rejected proposals --- instead of using generic review criteria.</p>
              </div>
              <div className="ir-info-card">
                <strong>Panel adapts to grant type</strong>
                <div className="ir-panel-chips">
                  {[
                    { label: 'NIH R01/R21', desc: 'Significance, Innovation, Approach, Investigator, Program Officer, Skeptic' },
                    { label: 'NSF',         desc: 'Intellectual Merit, Broader Impacts, Methods, Feasibility, Program Officer, Skeptic' },
                    { label: 'SBIR/STTR',   desc: 'Technical Feasibility, Commercial Potential, Team, Innovation/IP, Agency, Skeptic' },
                    { label: 'Custom',      desc: 'Upload reference proposals to get criteria specific to your program (e.g. DOT SS4A, USDA AFRI)' },
                  ].map(({ label, desc }) => (
                    <div key={label} className="ir-panel-chip">
                      <span className="ir-panel-chip-label">{label}</span>
                      <span className="ir-panel-chip-desc">{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Results panel ── */}
        {running && (
          <div className="ir-results-panel">
            <div className="ir-running">
              <FiLoader className="ir-spin ir-running-icon" />
              <p>
                {(acceptedFiles.length > 0 || rejectedFiles.length > 0)
                  ? 'Calibrating criteria from reference proposals, then running six reviewers in parallel...'
                  : 'Selecting panel for your grant type and running six reviewers in parallel...'
                }
              </p>
              <p className="ir-hint">
                {(acceptedFiles.length > 0 || rejectedFiles.length > 0)
                  ? 'This takes 60--90 seconds (calibration + review).'
                  : 'This takes 30--60 seconds.'
                }
              </p>
            </div>
          </div>
        )}

        {result && !running && (() => {
          const synthesis = result.synthesis || {};
          const reviewers = result.reviewers || [];
          const rankedIssues = synthesis.ranked_issues || [];
          const readiness = synthesis.overall_readiness || '';
          const refsUsed = Array.isArray(result.references_used) ? result.references_used : [];
          const refsFunded = refsUsed.filter(r => r.outcome === 'funded').length;
          const refsRejected = refsUsed.filter(r => r.outcome === 'rejected').length;
          const refsTagCounts = refsUsed.reduce((acc, r) => {
            (r.critique_tags || []).forEach(t => { acc[t] = (acc[t] || 0) + 1; });
            return acc;
          }, {});
          const topRefTags = Object.entries(refsTagCounts)
            .sort((a, b) => b[1] - a[1]).slice(0, 5).map(([t]) => t);
          const readinessClass = readiness.includes('Ready to Submit') ? 'ready'
            : readiness.includes('Minor') ? 'minor'
            : readiness.includes('Major') ? 'major'
            : 'not-ready';

          return (
            <div className="ir-results-panel">
              <div className="ir-results-header">
                <h2>Review Results</h2>
                <div className="ir-results-actions">
                  <button className="ir-btn ir-btn--outline" onClick={downloadMarkdown}>
                    <FiDownload /> Download .md
                  </button>
                  <button
                    className="ir-btn ir-btn--primary"
                    onClick={runReview}
                    disabled={!canSubmit}
                  >
                    <FiPlay /> Re-run
                  </button>
                </div>
              </div>

              {/* Readiness banner */}
              <div className={`ir-readiness ir-readiness--${readinessClass}`}>
                <strong>{readiness}</strong>
                {result.grant_type && <span className="ir-grant-type-badge">{result.grant_type}</span>}
                {result.criteria_source && (
                  <span className="ir-grant-type-badge" style={{ background: '#d1fae5', color: '#065f46' }}>
                    {result.criteria_source === 'corpus_calibrated' ? 'Calibrated from references' :
                     result.criteria_source === 'nofo_only' ? 'Calibrated from NOFO' : 'Calibrated'}
                  </span>
                )}
              </div>

              {/* Fix Issues CTA */}
              {onStartGrantAgent && rankedIssues.length > 0 && (
                <div className="ir-fix-cta">
                  <div className="ir-fix-cta-text">
                    <strong>
                      {highIssueCount > 0
                        ? `${highIssueCount} high-severity issue${highIssueCount !== 1 ? 's' : ''} found.`
                        : `${rankedIssues.length} issue${rankedIssues.length !== 1 ? 's' : ''} found.`
                      }
                    </strong>
                    <span>Open in Draft Proposal to auto-fix issues with one click.</span>
                  </div>
                  <button
                    className="ir-fix-cta-btn"
                    onClick={fixIssues}
                    disabled={creating}
                  >
                    {creating
                      ? <><FiLoader className="ir-spin" /> Creating...</>
                      : <><FiAlertTriangle /> Fix These Issues</>
                    }
                  </button>
                </div>
              )}

              {/* Top strengths */}
              {synthesis.top_strengths?.length > 0 && (
                <div className="ir-section">
                  <h3 className="ir-section-title ir-section-title--strengths">
                    <FiCheck /> Panel Strengths
                  </h3>
                  <ul className="ir-strengths-list">
                    {synthesis.top_strengths.map((s, i) => <li key={i}>{s}</li>)}
                  </ul>
                </div>
              )}

              {/* Ranked issues */}
              {rankedIssues.length > 0 && (
                <div className="ir-section">
                  <h3 className="ir-section-title ir-section-title--issues">
                    <FiAlertTriangle /> Priority Issues ({rankedIssues.length})
                  </h3>
                  {rankedIssues.map((issue, i) => (
                    <div key={i} className={`ir-issue ir-issue--${issue.severity}`}>
                      <div className="ir-issue-header">
                        <span className="ir-issue-number">#{i + 1}</span>
                        <span className={`ir-severity-badge ${SEVERITY_META[issue.severity]?.cls || ''}`}>
                          {SEVERITY_META[issue.severity]?.label || issue.severity}
                        </span>
                        <span className="ir-issue-section">{issue.section}</span>
                        <div className="ir-flagged-by">
                          {(issue.flagged_by || []).map(rid => {
                            const c = REVIEWER_COLORS[rid] || {};
                            return <span key={rid} className="ir-reviewer-dot" style={{ background: c.dot }} title={rid.replace(/_/g, ' ')} />;
                          })}
                        </div>
                      </div>
                      <p className="ir-issue-text">{issue.issue}</p>
                      {issue.fix && <p className="ir-issue-fix"><strong>Fix:</strong> {issue.fix}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Reference patterns — only renders when RAG is enabled + corpus returned hits */}
              {refsUsed.length > 0 && (
                <div className="ir-section">
                  <h3 className="ir-section-title ir-section-title--refs">
                    <FiFileText /> Reference patterns ({refsUsed.length})
                  </h3>
                  <div className="ir-refs-summary">
                    <span>
                      Grounded in <strong>{refsUsed.length}</strong> similar proposal{refsUsed.length !== 1 ? 's' : ''}
                      {(refsFunded > 0 || refsRejected > 0) && (
                        <> · {refsFunded} funded · {refsRejected} rejected</>
                      )}
                    </span>
                    <button
                      className="ir-refs-toggle"
                      onClick={() => setRefsExpanded(v => !v)}
                    >
                      {refsExpanded
                        ? <><FiChevronDown /> Hide details</>
                        : <><FiChevronRight /> Show details</>}
                    </button>
                  </div>
                  {topRefTags.length > 0 && (
                    <div className="ir-refs-tag-row">
                      <span className="ir-refs-tag-label">Concern patterns in this set:</span>
                      {topRefTags.map(t => (
                        <span key={t} className="ir-refs-tag">{t}</span>
                      ))}
                    </div>
                  )}
                  {refsExpanded && (
                    <div className="ir-refs-list">
                      {refsUsed.map((ref, i) => (
                        <div key={`${ref.proposal_id}-${ref.section_type}-${i}`} className={`ir-ref ir-ref--${ref.outcome || 'unknown'}`}>
                          <div className="ir-ref-header">
                            <span className="ir-ref-index">#{i + 1}</span>
                            <span className="ir-ref-section">{ref.section_type}</span>
                            {ref.grant_mechanism && <span className="ir-ref-mech">{ref.grant_mechanism}</span>}
                            <span className={`ir-ref-outcome ir-ref-outcome--${ref.outcome || 'unknown'}`}>
                              {ref.outcome || 'unknown'}
                            </span>
                            {typeof ref.impact_score === 'number' && (
                              <span className="ir-ref-impact">impact {ref.impact_score}</span>
                            )}
                            {typeof ref.similarity === 'number' && (
                              <span className="ir-ref-sim">similarity {ref.similarity.toFixed(2)}</span>
                            )}
                          </div>
                          {(ref.critique_tags || []).length > 0 && (
                            <div className="ir-ref-tags">
                              {ref.critique_tags.map(t => (
                                <span key={t} className="ir-refs-tag ir-refs-tag--sm">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Individual reviewer cards */}
              <div className="ir-section">
                <h3 className="ir-section-title">Individual Reviewer Feedback</h3>
                {reviewers.map(reviewer => {
                  const c = REVIEWER_COLORS[reviewer.id] || { bg: '#f9fafb', border: '#e5e7eb', text: '#374151', dot: '#6b7280' };
                  const isOpen = !!expandedReviewers[reviewer.id];
                  const highCount = (reviewer.issues || []).filter(i => i.severity === 'high').length;
                  const reviewerRefCount = Array.isArray(reviewer.references_used) ? reviewer.references_used.length : 0;
                  return (
                    <div key={reviewer.id} className="ir-reviewer-card" style={{ borderColor: c.border }}>
                      <div
                        className="ir-reviewer-card-header"
                        style={{ background: c.bg }}
                        onClick={() => setExpandedReviewers(s => ({ ...s, [reviewer.id]: !s[reviewer.id] }))}
                        role="button" tabIndex={0}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setExpandedReviewers(s => ({ ...s, [reviewer.id]: !s[reviewer.id] }))}
                      >
                        <span className="ir-reviewer-dot-lg" style={{ background: c.dot }} />
                        <span className="ir-reviewer-label" style={{ color: c.text }}>{reviewer.label}</span>
                        {highCount > 0 && <span className="ir-reviewer-high-count">{highCount} high</span>}
                        <span className="ir-reviewer-issue-count">{(reviewer.issues || []).length} issue{(reviewer.issues || []).length !== 1 ? 's' : ''}</span>
                        {reviewerRefCount > 0 && (
                          <span className="ir-reviewer-ref-count" title="Reference patterns retrieved for this reviewer">
                            · {reviewerRefCount} ref{reviewerRefCount !== 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="ir-reviewer-chevron" style={{ color: c.text }}>
                          {isOpen ? <FiChevronDown /> : <FiChevronRight />}
                        </span>
                      </div>
                      {isOpen && (
                        <div className="ir-reviewer-card-body">
                          {reviewer.overall_comment && (
                            <p className="ir-reviewer-comment">{reviewer.overall_comment}</p>
                          )}
                          {(reviewer.strengths || []).length > 0 && (
                            <div className="ir-reviewer-strengths">
                              <strong>Strengths:</strong>
                              <ul>{reviewer.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                            </div>
                          )}
                          {(reviewer.issues || []).map((issue, i) => (
                            <div key={i} className={`ir-issue ir-issue--${issue.severity}`}>
                              <div className="ir-issue-header">
                                <span className={`ir-severity-badge ${SEVERITY_META[issue.severity]?.cls || ''}`}>
                                  {SEVERITY_META[issue.severity]?.label || issue.severity}
                                </span>
                                <span className="ir-issue-section">{issue.section}</span>
                              </div>
                              <p className="ir-issue-text">{issue.issue}</p>
                              {issue.fix && <p className="ir-issue-fix"><strong>Fix:</strong> {issue.fix}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <p className="ir-footer-note">
                Run at {new Date(result.run_at).toLocaleString()} · Not a score prediction.
                Want the full pipeline? <strong>Draft Proposal</strong> builds on this with iterative revision.
              </p>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
